import {assert} from 'Asserts';
import {LegacyAssaySchemaType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  AutomationInputFileConfigSpec,
  ClientAutomationOutputFileConfigSpec,
} from 'lab-automation/settings/RunSchemaSpecConstants';
import {getStore} from 'redux-stores/Store';
import {ClientSchemaField} from 'registry/field/FieldTable';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {massageFieldsForSubmit} from 'schemas/SchemaUtil';
import {getServerOutputFileConfigs} from 'schemas/spec/reduceSchemaConfigUI';
import {AssaySchemaSpec, RunSchemaSpec} from 'schemas/spec/SchemaConfigConstants';
import * as SchemaConfigReduxActions from 'schemas/spec/SchemaConfigReduxActions';
import {Filter} from 'search/SearchConstants';
import {FieldNamingException} from 'warehouse/nameToSqlIdentifier';

export interface AssayRunSchemaSpecOverrides {
  analysisTemplateId?: string | null;
  runFields?: Array<ClientSchemaField | SchemaField>;
  templateFields?: Array<ClientSchemaField | SchemaField>;
  analysisTemplateVariableMapping?: Record<string, string> | null;
  analysisTemplateDefaultFiltersMapping?: Record<string, object> | null;
}

export interface SchemaSpecRequestProps {
  schemaType: LegacyAssaySchemaType.ASSAY_RESULT | LegacyAssaySchemaType.ASSAY_RUN;
  fields: Array<ClientSchemaField | SchemaField> | null;
  inputFileConfigs: Array<AutomationInputFileConfigSpec>;
  outputFileConfigs: Array<ClientAutomationOutputFileConfigSpec>;
  allowCreateRunFromNotebook: boolean;
  allowInsertRunFromInbox: boolean;
  shouldRecordEquipment: boolean;
  connectionSchemaId: string | null;
  analysisTemplateId: string | null;
  analysisTemplateVariableMapping: Record<string, string> | null;
  analysisTemplateDefaultFiltersMapping: Record<string, Array<Filter>> | null;
}

export function massageFieldsForSubmitWithSpecErrorHandling(
  fields: Array<ClientSchemaField | SchemaField>
): Array<SchemaField> | null {
  try {
    return massageFieldsForSubmit(fields);
  } catch (e) {
    if (e instanceof FieldNamingException) {
      getStore().dispatch(
        SchemaConfigReduxActions.setSchemaSpecError({
          errors: [],
          errorMessage: e.message,
        })
      );
      return null;
    } else {
      throw e;
    }
  }
}

export function getSpecForRequest(
  props: SchemaSpecRequestProps,
  overrides?: AssayRunSchemaSpecOverrides | null
): AssaySchemaSpec | RunSchemaSpec | null {
  // Use override fields if provided, otherwise use props.fields
  const allFields =
    overrides?.runFields && overrides?.templateFields
      ? [...overrides.runFields, ...overrides.templateFields]
      : props.fields;

  assert(allFields != null, '"fields" must be defined before constructing the spec.');
  if (allFields?.map((field) => field.name).includes('')) {
    getStore().dispatch(
      SchemaConfigReduxActions.setSchemaSpecError({
        errors: [],
        errorMessage: `"fields" must all have names.`,
      })
    );
    return null;
  }
  const fields = massageFieldsForSubmitWithSpecErrorHandling(allFields);
  if (fields == null) {
    return null;
  }

  if (props.schemaType === LegacyAssaySchemaType.ASSAY_RUN) {
    const spec = {
      fields,
      automationInputFileConfigs: props.inputFileConfigs,
      allowCreateRunFromNotebook: props.allowCreateRunFromNotebook,
      allowInsertRunFromInbox: props.allowInsertRunFromInbox,
      shouldRecordEquipment: props.shouldRecordEquipment,
      connectionSchemaId: props.connectionSchemaId,
      // Use override if provided, otherwise fall back to props value
      analysisTemplateId:
        overrides != null && overrides.analysisTemplateId !== undefined
          ? overrides.analysisTemplateId
          : props.analysisTemplateId,
      analysisTemplateVariableMapping:
        overrides != null && overrides.analysisTemplateVariableMapping !== undefined
          ? overrides.analysisTemplateVariableMapping
          : props.analysisTemplateVariableMapping,
      analysisTemplateDefaultFiltersMapping:
        overrides != null && overrides.analysisTemplateDefaultFiltersMapping !== undefined
          ? overrides.analysisTemplateDefaultFiltersMapping
          : props.analysisTemplateDefaultFiltersMapping,
    };
    return {
      ...spec,
      automationOutputFileConfigs: getServerOutputFileConfigs(props.outputFileConfigs),
    };
  } else {
    return {fields};
  }
}
